/**
 * Where deleted files go, and what is recorded about them.
 *
 * The trash used to live at `.notespace/trash`. The leading dot hid it from
 * Finder, so the only way to reach a deleted note was the command line — a
 * recycle bin nobody can open is a recycle bin that doesn't exist. It sits at
 * the root of the vault now under a plain name, reachable with the app closed,
 * and `isHiddenFolderPath` keeps it out of the sidebar the same way it keeps
 * `attachments` out: present on disk, absent from the file list.
 *
 * Constants and pure helpers only, so both the file layer and the presentation
 * layer can name the folder without importing each other.
 */

/** Root-level folder holding deleted entries. Visible on disk by design. */
export const TRASH_DIR = "Trash";

/** What was deleted, from where, and when — read by the in-app trash view. */
export const TRASH_INDEX = `${TRASH_DIR}/index.json`;

/** The hidden pre-existing location. Older vaults are migrated out of it on
 *  open; nothing writes here any more. */
export const LEGACY_TRASH_DIR = ".notespace/trash";

/** One trashed entry. Each lives in its own `TRASH_DIR/<id>/` folder, so it
 *  keeps its exact original name and can be dragged back in Finder as-is. */
export interface TrashEntry {
  id: string;
  /** Vault-relative path it was deleted from — where "Restore" puts it back. */
  path: string;
  name: string;
  kind: "file" | "folder";
  deletedAt: number;
}

/** True for the trash folder itself and everything inside it. */
export function isTrashPath(path: string): boolean {
  return path === TRASH_DIR || path.startsWith(`${TRASH_DIR}/`);
}
